"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCartStore } from "@/lib/store/cart-store";
import * as cartService from "@/services/cart";
import { AddToCartPayload, UpdateCartItemPayload } from "@/lib/types/cart";
import { CART_QUERY_KEY } from "@/lib/constants";
import { toast } from "sonner";
import { useSession } from "next-auth/react";

export function useCart() {
  const { data: session } = useSession();
  const userId = session?.user?.id;

  const {
    items,
    totalItems,
    totalPrice,
    addItem,
    revertAddItem,
    removeItem,
    revertRemoveItem,
    updateQuantity,
    revertUpdateQuantity,
    clearCart: clearCartStore,
    getItemById,
    hasItem,
  } = useCartStore();

  const queryClient = useQueryClient();

  const addToCartMutation = useMutation({
    mutationFn: (payload: AddToCartPayload) => cartService.addToCart(payload, userId),
    onMutate: async (payload: AddToCartPayload) => {
      // 1. Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: [CART_QUERY_KEY] });

      // 2. Optimistically update Zustand (instant UI feedback)
      const itemId = addItem(payload);

      // 3. Return context for potential rollback
      return { itemId, payload };
    },
    onError: (error, variables, context) => {
      // Rollback using Zustand revert action
      if (context?.itemId) {
        revertAddItem(context.itemId);
      }
      toast.error("Failed to add item to cart");
      console.error("Add to cart error:", error);
    },
    onSuccess: (response) => {
      if (response.error) {
        toast.error(response.error);
      } else {
      }
    },
    onSettled: () => {
      // Sync with server
      queryClient.invalidateQueries({ queryKey: [CART_QUERY_KEY] });
    },
  });

  const updateQuantityMutation = useMutation({
    mutationFn: (payload: UpdateCartItemPayload) => cartService.updateCartItem(payload, userId),
    onMutate: async ({ itemId, quantity }) => {
      await queryClient.cancelQueries({ queryKey: [CART_QUERY_KEY] });

      // Save previous value for rollback
      const previousQuantity = getItemById(itemId)?.quantity;

      // Optimistically update
      if (quantity) {
        updateQuantity(itemId, quantity);
      }

      return { itemId, previousQuantity };
    },
    onError: (error, variables, context) => {
      if (context?.previousQuantity) {
        revertUpdateQuantity(context.itemId, context.previousQuantity);
      }
      toast.error("Failed to update quantity");
      console.error("Update quantity error:", error);
    },
    onSuccess: (response) => {
      if (response.error) {
        toast.error(response.error);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CART_QUERY_KEY] });
    },
  });

  const removeFromCartMutation = useMutation({
    mutationFn: (itemId: string) => cartService.removeFromCart(itemId, userId),
    onMutate: async (itemId: string) => {
      await queryClient.cancelQueries({ queryKey: [CART_QUERY_KEY] });

      // Save full item for potential restore
      const removedItem = getItemById(itemId);

      // Optimistically remove
      removeItem(itemId);

      return { removedItem };
    },
    onError: (error, variables, context) => {
      // Restore the removed item
      if (context?.removedItem) {
        revertRemoveItem(context.removedItem);
      }
      toast.error("Failed to remove item");
      console.error("Remove from cart error:", error);
    },
    onSuccess: (response) => {
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success("Item removed from cart");
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CART_QUERY_KEY] });
    },
  });

  const clearCartMutation = useMutation({
    mutationFn: () => cartService.clearCart(userId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: [CART_QUERY_KEY] });

      // Save previous cart for rollback
      const previousItems = [...items];

      // Optimistically clear
      clearCartStore();

      return { previousItems };
    },
    onError: (error, variables, context) => {
      // Restore previous cart
      if (context?.previousItems) {
        context.previousItems.forEach((item) => {
          addItem({
            productId: item.productId,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            image: item.image,
            tags: item.tags,
            variants: item.variants,
            stock: item.stock,
          });
        });
      }
      toast.error("Failed to clear cart");
      console.error("Clear cart error:", error);
    },
    onSuccess: (response) => {
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success("Cart cleared");
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CART_QUERY_KEY] });
    },
  });

  return {
    // Cart state
    items,
    totalItems,
    totalPrice,

    // Mutations
    addToCart: addToCartMutation.mutate,
    addToCartAsync: addToCartMutation.mutateAsync,
    updateCartQuantity: updateQuantityMutation.mutate,
    updateCartQuantityAsync: updateQuantityMutation.mutateAsync,
    removeFromCart: removeFromCartMutation.mutate,
    removeFromCartAsync: removeFromCartMutation.mutateAsync,
    clearCart: clearCartMutation.mutate,
    clearCartAsync: clearCartMutation.mutateAsync,

    // Loading states
    isAdding: addToCartMutation.isPending,
    isUpdating: updateQuantityMutation.isPending,
    isRemoving: removeFromCartMutation.isPending,
    isClearing: clearCartMutation.isPending,

    // Utilities
    getItemById,
    hasItem,
  };
}
